import { z } from 'zod'
import { type Expand } from '@/types/ui/expand'
import { ENGAGEMENT_BILLING_TYPE, ENGAGEMENT_TYPE } from '@/lib/constants/engagement-codes'
import { zodValidationHelpers } from '@/lib/utils/zod-validation-helpers'
import type { TaskCoverage } from '@/lib/utils/worker-pay-shape'
import {
  duplicateDates,
  effectiveLastDay,
  entryProblems,
  isPending,
  toDateOnly,
  todayDateOnly,
} from '@/lib/utils/scheduled-budgeted-hours'

// Worker type constants
export const WORKER_TYPE = {
  T4_SALARIED: "t4Salaried",
  REGULAR: "regular"
} as const

export type WorkerType = typeof WORKER_TYPE[keyof typeof WORKER_TYPE]

// Worker legacy sync status constants
export const WORKER_LEGACY_SYNC_STATUS = {
  READY: "Ready",
  ERROR: "Error", 
  PROCESSING: "Processing"
} as const

export type WorkerFormLegacySyncStatus = typeof WORKER_LEGACY_SYNC_STATUS[keyof typeof WORKER_LEGACY_SYNC_STATUS] | undefined

// Base schema for worker form data
/**
 * One rate selected from the worker's profile. The amount, the unit and the name are echoed from the
 * profile and rendered read-only, so nothing here is authored on the engagement. The rate type is this
 * engagement's slot allocation and is never shown.
 *
 * Billability is the exception: it is the one thing the engagement decides about a rate, since the same
 * rate can be billable on one placement and not on another.
 */
const workerPayRateSchema = z.object({
  profileRateId: z.string().optional().nullable(),
  rateTypeCode: z.string().optional().nullable(),
  payRate: z.number().optional().nullable(),
  payRateUnitCode: z.string().optional().nullable(),
  description: z.string().optional().nullable(),
  scheduledPayRate: z.number().optional().nullable(),
  scheduledPayRateEffectiveDate: z.string().optional().nullable(),
  // Left absent rather than defaulted, so the form's shape matches the stored one. Allocation is what
  // decides a rate's billability, and it is what supplies the default.
  billable: z.boolean().optional(),
  // Derived on save from the engagement's own Bill Rate, so the form carries them without offering them.
  billRate: z.number().optional().nullable(),
  billRateUnitCode: z.string().optional().nullable(),
  // Only ever arrives from the API. Nothing on this form creates a derived row, so it is left absent
  // rather than defaulted, which would make every rate the form builds claim to be one.
})

/**
 * A dated change to the worker's budgeted hours per day. Dates stay as the engagement stores them
 * (yyyy-MM-dd), so they compare as strings and round-trip without a timezone shift. The stamps are the
 * service's and are only carried, so a save sends back what it loaded.
 */
const scheduledBudgetedHoursEntrySchema = z.object({
  id: z.string().optional().nullable(),
  effectiveDate: z.string(),
  hoursPerDay: z.number(),
  appliedDate: z.string().optional().nullable(),
  cancelledDate: z.string().optional().nullable(),
})

export type ScheduledBudgetedHoursEntryFormData = z.infer<typeof scheduledBudgetedHoursEntrySchema>

const baseWorkerSchema = z.object({
  workerUserProfileId: z.number().min(1, 'Field is required'),
  type: z.enum([WORKER_TYPE.T4_SALARIED, WORKER_TYPE.REGULAR]).optional(), // For display purposes only, not used for validation
  jobCategoryCode: z.string().min(1, 'Field is required'),
  jobFunctionCode: z.string().min(1, 'Field is required'),
  jobTitle: z.string().min(1, 'Field is required'), // Free text field - required
  lineOfBusinessCode: z.string().optional().nullable(), // Read-only, from profile
  budgetedHours: z.number().nullable(),
  scheduledBudgetedHours: z.array(scheduledBudgetedHoursEntrySchema).default([]),
  billRate: z.number().nullable(),
  billRateUnit: z.string().optional(),
  reportingBillRate: z.number().nullable(),
  // Every rate this worker is paid at here, in the order they read. payRate, payRateUnit and the
  // scheduled fields below mirror the first entry, so the redactor, the audit config
  // and every other reader of the single pay rate carry on unchanged.
  payRates: z.array(workerPayRateSchema).default([]),
  payRate: z.number().nullable(),
  payRateUnit: z.string().optional(),
  scheduledPayRate: z.number().optional().nullable(), // Scheduled pay rate for future rate changes
  scheduledPayRateEffectiveDate: z.string().optional().nullable(), // ISO date string for when scheduled rate takes effect
  scheduledReportingPayRate: z.number().optional().nullable(), // Scheduled reporting pay rate for T4 Salaried workers
  scheduledReportingPayRateEffectiveDate: z.string().optional().nullable(), // ISO date string for when scheduled reporting rate takes effect
  reportingPayRate: z.number().nullable(),
  paySchedule: z.string().optional(),
  paymentCurrencyCode: z.string().optional().nullable(),
  legacySyncStatus: z.enum([WORKER_LEGACY_SYNC_STATUS.READY, WORKER_LEGACY_SYNC_STATUS.ERROR, WORKER_LEGACY_SYNC_STATUS.PROCESSING]).optional(), // For display purposes only, not used for validation
  legacySyncError: z.string().optional().nullable(), // Per-worker sync error detail shown under an Error badge (display only)
  isRemoved: z.boolean().default(false), // Track if worker has been removed in change requests
  isNew: z.boolean().default(false), // Track if worker is new in change requests (for validation)
  firstDayOfWork: z.date().nullable(), // ISO date format - when worker assignment begins (required)
  lastDayOfWork: z.date().optional().nullable(), // per-worker end date (active: winds down early; removed: termination date)
  terminationTypeId: z.number().optional().nullable(), // Set when the removal was an actual termination (display only)
  terminationReasonIds: z.array(z.number()).optional().nullable(), // Saved termination reason(s); distinguishes Terminated from Removed (display only)
  originalStartDate: z.string().optional().nullable(), // Set when worker has been extended; indicates original start date before extension
  extensionStartDate: z.string().optional().nullable(), // New start date from extension
  extensionEndDate: z.string().optional().nullable(), // New end date from extension
})

// Function to create schema with conditional validation based on engagement context (optimized)
export const createWorkerFormSchema = (
  billingType?: string,
  engagementType?: string,
  engagementStartDate?: Date,
  engagementEndDate?: Date,
  // When the user cannot view pay rates, the backend redacts them (Pay Rate / Reporting Pay Rate)
  // and the UI hides those inputs. Requiring them would flag the worker as incomplete for a field
  // the user can neither see nor fill, so the pay-rate requirements are skipped in that case. The
  // backend write guard preserves the persisted values regardless.
  canViewPayRates: boolean = true,
  // What the engagement's active tasks need a worker to be paid at. Left out, no claim is made about
  // whether the worker's rates cover the tasks.
  taskCoverage?: TaskCoverage
) => {
  return baseWorkerSchema.superRefine((data, ctx) => {
    // Cache worker type to avoid repeated property access
    const { type } = data

    // Whether the form shows a Bill Rate at all.
    const isBillRateRequired = billingType !== ENGAGEMENT_BILLING_TYPE.MILESTONE && engagementType !== ENGAGEMENT_TYPE.INTERNAL

    // Job title validation - always required
    if (!data.jobTitle || data.jobTitle.trim() === '') {
      zodValidationHelpers.requiredString(ctx, ['jobTitle'])
    }
    
    // Budgeted hours validation - required (0 is allowed), must not be negative, max 24
    if (data.budgetedHours == null) {
      zodValidationHelpers.requiredDecimal(ctx, ['budgetedHours'], 'Field is required')
    } else if (data.budgetedHours < 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        minimum: 0,
        type: 'number',
        inclusive: true,
        message: 'Cannot be negative',
        path: ['budgetedHours'],
      })
    } else if (data.budgetedHours > 24) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_big,
        maximum: 24,
        type: 'number',
        inclusive: true,
        message: 'Cannot exceed 24 hours per day',
        path: ['budgetedHours'],
      })
    }

    // Scheduled budgeted hours changes. Only a change not yet saved is held to the date and hours rules,
    // as the service does: a saved one was checked when it was added, and may since have fallen behind
    // today or past a shortened Last Day of Work without anything being wrong with it. A removed
    // worker's pending changes are cancelled by the save, so there is nothing to check.
    if (!data.isRemoved) {
      const bounds = {
        today: todayDateOnly(),
        firstDay: data.firstDayOfWork
          ? toDateOnly(data.firstDayOfWork)
          : engagementStartDate ? toDateOnly(engagementStartDate) : null,
        lastDay: effectiveLastDay(data.lastDayOfWork, engagementEndDate),
      }

      data.scheduledBudgetedHours.forEach((entry, index) => {
        if (entry.id || !isPending(entry)) {
          return
        }
        for (const problem of entryProblems(entry, bounds)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: problem.message,
            path: ['scheduledBudgetedHours', index, problem.field],
          })
        }
      })

      for (const date of duplicateDates(data.scheduledBudgetedHours)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `More than one budgeted hours change takes effect on ${date}.`,
          path: ['scheduledBudgetedHours'],
        })
      }
    }

    // Bill rate validation (only when required)
    if (isBillRateRequired) {
      if (!data.billRate || data.billRate <= 0) {
        zodValidationHelpers.requiredDecimal(ctx, ['billRate'])
      }
      if (!data.billRateUnit || data.billRateUnit.trim() === '') {
        zodValidationHelpers.requiredString(ctx, ['billRateUnit'])
      }
    }

// Worker type specific validations
    if (type === WORKER_TYPE.T4_SALARIED) {
      if (canViewPayRates && (!data.reportingPayRate || data.reportingPayRate <= 0)) {
        zodValidationHelpers.requiredDecimal(ctx, ['reportingPayRate'])
      }
    } else if (type === WORKER_TYPE.REGULAR) {
      // Pay Rate and its unit share the redacted/hidden block, so both are only required when visible.
      if (canViewPayRates && (!data.payRate || data.payRate <= 0)) {
        zodValidationHelpers.requiredDecimal(ctx, ['payRate'])
      }
      if (canViewPayRates && (!data.payRateUnit || data.payRateUnit.trim() === '')) {
        zodValidationHelpers.requiredString(ctx, ['payRateUnit'])
      }
      // Pay Schedule stays visible regardless of pay-rate visibility, so it remains required.
      if (!data.paySchedule || data.paySchedule.trim() === '') {
        zodValidationHelpers.requiredString(ctx, ['paySchedule'])
      }
      // Which rates a worker needs is whatever their tasks can record against, and this schema cannot
      // see the task list. Holding none is the one case it can answer without it: every reading of the
      // rule asks for at least one, so an empty list is wrong whatever the engagement's tasks turn out
      // to be. Left to a reader who cannot see the rates, since they can neither read the list nor fix
      // it, and the save says the same thing for them.
      if (canViewPayRates && data.payRates.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Select at least one rate this worker is paid at.',
          path: ['payRates'],
        })
      }
      // A selected rate with no amount on the profile cannot be paid, and the engagement has no way to
      // give it one, so the message names the rate and sends the user to the profile rather than
      // reporting a blank field they cannot fill.
      if (canViewPayRates) {
        data.payRates.forEach((rate, index) => {
          if (rate.payRate == null || rate.payRate <= 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `${rate.description?.trim() || 'This rate'} has no amount. Set it on the worker profile before continuing.`,
              path: ['payRates', index, 'payRate'],
            })
          }
        })
      }
      // One rate per slot. How many rates a worker can hold is the size of the family their category
      // draws from, which this schema cannot see, but two rates sharing a slot is checkable here and is
      // what allocation running out of room would look like. The picker stops offering a rate whose
      // family is full, so reaching either means something bypassed it.
      const slotCounts = new Map<string, number>()
      data.payRates.forEach(rate => {
        const slot = rate.rateTypeCode?.toLowerCase()
        if (slot) {
          slotCounts.set(slot, (slotCounts.get(slot) ?? 0) + 1)
        }
      })

      if ([...slotCounts.values()].some(count => count > 1)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'A worker cannot be paid at two rates of the same kind on one engagement.',
          path: ['payRates'],
        })
      }
      // A task is only covered while the worker holds a rate of its billability, since hours land on a
      // rate whose billability matches their task's. Mirrors ValidateWorkerPayRates on the server, wording
      // included, so the message reads the same whichever side raises it. An empty list is reported
      // above, and a removed worker's rates record what they were paid rather than what the tasks need.
      if (canViewPayRates && taskCoverage && !data.isRemoved && data.payRates.length > 0) {
        // A rate bills only while the worker has a Bill Rate. The save sends none on an engagement that
        // hides the field, whatever the form holds, so there every rate counts as non-billable. A rate
        // carrying no billability is saved as billable.
        const holdsNonBillable = data.payRates.some(rate => !(isBillRateRequired && rate.billable !== false))
        const holdsBillable = data.payRates.some(rate => rate.billable !== false)

        if (taskCoverage.needsNonBillable && !holdsNonBillable) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'This engagement has an active non-billable task. Add a non-billable rate to this worker. If they hold none, add one to their profile.',
            path: ['payRates'],
          })
        }
        if (taskCoverage.needsBillable && !holdsBillable) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'This engagement has an active billable task. Add a billable rate to this worker. If they hold none, add one to their profile.',
            path: ['payRates'],
          })
        }
      }
    }

    // Scheduled rate validations - optional but must be positive if provided
    if (data.scheduledPayRate != null && data.scheduledPayRate <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Scheduled pay rate must be a positive number',
        path: ['scheduledPayRate'],
      })
    }
    if (data.scheduledReportingPayRate != null && data.scheduledReportingPayRate <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Scheduled reporting pay rate must be a positive number',
        path: ['scheduledReportingPayRate'],
      })
    }

    // First day of work validation - always required
    if (!data.firstDayOfWork) {
      zodValidationHelpers.requiredDate(ctx, ['firstDayOfWork'])
    }

    // Last day of work validation - required for removed workers in change requests
    if (data.isRemoved && !data.lastDayOfWork) {
      zodValidationHelpers.requiredDate(ctx, ['lastDayOfWork'])
    }

    // Date range validations (only if both dates exist - matching core form pattern)
    // Allow same-day termination (last day == first day): a worker terminated effective their
    // start date is valid and the backend permits it. Only a last day strictly before the first
    // day is invalid. Using <= here previously blocked re-editing an engagement that had a
    // same-day-terminated worker, since the form re-validates every worker including removed ones.
    if (data.firstDayOfWork && data.lastDayOfWork && data.lastDayOfWork < data.firstDayOfWork) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Last day of work must be on or after first day of work',
        path: ['lastDayOfWork'],
      })
    }

    // Engagement period validation - only validate if engagement dates are provided (performance optimization)
    if (engagementStartDate && engagementEndDate) {
      // First day of work validations (only if first day exists)
      if (data.firstDayOfWork) {
        if (data.firstDayOfWork < engagementStartDate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'First day of work cannot be before engagement start',
            path: ['firstDayOfWork'],
          })
        }
        if (data.firstDayOfWork > engagementEndDate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'First day of work cannot be after engagement end',
            path: ['firstDayOfWork'],
          })
        }
      }
      
      // Last day of work validations (only if last day exists)
      if (data.lastDayOfWork) {
        if (data.lastDayOfWork < engagementStartDate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Last day of work cannot be before engagement start',
            path: ['lastDayOfWork'],
          })
        }
        if (data.lastDayOfWork > engagementEndDate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Last day of work cannot be after engagement end',
            path: ['lastDayOfWork'],
          })
        }
      }
    }
  })
}

// Schema for the worker form with conditional validation
export const workerFormSchema = createWorkerFormSchema()

// Function to create worker form array schema with conditional validation
export const createWorkerFormArraySchema = (billingType?: string, engagementType?: string, engagementStartDate?: Date, engagementEndDate?: Date, canViewPayRates: boolean = true, taskCoverage?: TaskCoverage) => {
  return z.object({
    workers: z.array(createWorkerFormSchema(billingType, engagementType, engagementStartDate, engagementEndDate, canViewPayRates, taskCoverage))
      .min(1, 'At least one worker is required')
      .superRefine((workers, ctx) => {
        // Ensure at least one worker is not removed (active)
        const activeWorkers = workers.filter(worker => !worker.isRemoved)
        if (activeWorkers.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'This engagement has no active workers.',
            path: []
          })
        }
      })
  })
}

// Schema for the worker form array
export const workerFormArraySchema = createWorkerFormArraySchema()

// Automatically infer the TypeScript type from the schema
export type WorkerFormData = Expand<z.infer<typeof workerFormSchema>>
export type WorkerFormArrayData = Expand<z.infer<typeof workerFormArraySchema>> 